import { KeyboardArrowLeft, KeyboardArrowRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from "@mui/icons-material";
import { FormControlLabel, IconButton, Stack, Switch, Typography } from "@mui/material";
import { ColDef, GetRowIdParams, ICellRendererParams, RowClassRules, RowDataUpdatedEvent, RowNode, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import _ from "underscore";
import { useAgGridLocale as getAgGridLocale } from "../../../../../../config/globalConfig";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import { AgGridCustomToolbarProps } from "../../../../../../util/component/AgGridCustomToolbar";
import { useCarsAvailabilitiesQuery } from "../../../../api/services";
import { isBetweenDates } from "../../../../utils";
import { carsAvailabilitiesParamsState, totalRentalDaysState, isSelectedCarGroupUnavailableState } from "../../recoil";
import { IFormValues, ITableAvailabilitiesFilter, ITableAvailabilitiesRow } from "../../types";
import CellAvailabilities from "./table-cell/CellAvailabilities";

const HEADER_FIELD_DATE_FORMAT = 'DD/MM/YYYY';

const TableAvailabilities = () => {

    const { t } = useTranslation('booking');

    const gridRef = useRef<AgGridReact>(null);

    const totalRentalDays = useRecoilValue(totalRentalDaysState);
    const carsAvailabilitiesParams = useRecoilValue(carsAvailabilitiesParamsState);

    const formContext = useFormContext<IFormValues>();

    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });
    const carGroup = useWatch({ control: formContext.control, name: "basicDetails.carGroup" });
    const setIsSelectedCarGroupUnavailableGlobal = useSetRecoilState(isSelectedCarGroupUnavailableState);

    const pickupDate = useWatch<IFormValues, "basicDetails.pickupDate">({
        name: 'basicDetails.pickupDate'
    });
    const dropoffDate = useWatch<IFormValues, "basicDetails.dropoffDate">({
        name: 'basicDetails.dropoffDate'
    });
    const lockGroup = useWatch<IFormValues, "basicDetails.lockGroup">({
        name: 'basicDetails.lockGroup'
    });

    const carsAvailabilitiesQuery = useCarsAvailabilitiesQuery<ITableAvailabilitiesRow[]>(
        carsAvailabilitiesParams,
        {
            enabled: Boolean(carsAvailabilitiesParams),
            refetchOnMount: false,
            retryOnMount: false,
            select: (data) => {
                if (!data) {
                    return [];
                }
                const lockGroup = formContext.getValues('basicDetails.lockGroup');
                const carAvailabilities = lockGroup ? data?.locationAvailabilities : data?.carAvailabilities;
                return carAvailabilities
                    .map((item) => {
                        if (!item)
                            return null;
                        const dateValues: any = {};
                        item?.availabilities?.forEach(a => {
                            if (!a || !a.date)
                                return;
                            dateValues[dayjs(a.date).format(HEADER_FIELD_DATE_FORMAT)] = a;
                        });
                        return {
                            code: item?.code || '',
                            label: item?.label || '',
                            ...dateValues
                        }
                    });
            },
            onSuccess: (data) => {
                setRowData([...data]);
                gridRef.current?.api.redrawRows();
            },
            onError: () => {
                setRowData([]);
            },
        }
    );

    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
    const [rowData, setRowData] = useState<ITableAvailabilitiesRow[]>(carsAvailabilitiesQuery.data ?? []);
    const [tableActiveFilters, setTableActiveFilters] = useState<ITableAvailabilitiesFilter>({
        onlyAvailable: false
    });
    const [dateList, setDateList] = useState<dayjs.Dayjs[]>([]);

    const agGridLocaleText = useMemo(() => getAgGridLocale(), []);
    const defaultColDef = useMemo<ColDef>(() => ({
        cellStyle: {
            display: 'flex',
            alignItems: 'center'
        },
        width: 400,
        suppressMenu: true,
        suppressMovable: true
    }), []);
    const rowClassRules = useMemo<RowClassRules>(() => ({
        'ag-row-selected': (params) => {
            const lockGroup = formContext.getValues('basicDetails.lockGroup');
            const pickupLocation = formContext.getValues('basicDetails.pickupLocation');
            const carGroup = formContext.getValues('basicDetails.carGroup');
            return lockGroup ? params.data.code === pickupLocation?.id : params.data.code === carGroup?.acrissCode;
        }
    }), []);
    const minDate = useMemo(() => {
        if (!pickupDate)
            return null;
        const today = dayjs();
        const defaultMin = pickupDate.add(-15, "day");
        return today.isAfter(defaultMin) ? today : defaultMin;
    }, [pickupDate]);
    const maxDate = useMemo(() => {
        if (!dropoffDate)
            return null;
        return dropoffDate.add(15, "day");
    }, [dropoffDate]);
    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderActions: () => (
            <Stack direction="row" columnGap={4} py={1}>
                <FormControlLabel
                    labelPlacement="start"
                    label={<Typography variant="body2">{t('label.showOnlyAvailable')}</Typography>}
                    sx={{ columnGap: 1, userSelect: 'none' }}
                    control={
                        <Switch onChange={(e, checked) => setFilter("onlyAvailable", checked)} />
                    }
                />
                <Stack direction="row" columnGap={1}>
                    <IconButton
                        sx={{ p: 0 }}
                        disabled={dateList.length === 0 || (dateList.length !== 0 && dateList[0].add(-7, "day").isBefore(minDate, 'd'))}
                        onClick={() => setDateList(current => {
                            return current.map(date => date.add(-7, "day"))
                        })}
                    >
                        <KeyboardDoubleArrowLeft fontSize="small" />
                    </IconButton>
                    <IconButton
                        sx={{ p: 0 }}
                        disabled={dateList.length === 0 || (dateList.length !== 0 && dateList[0].add(-1, "day").isBefore(minDate, 'd'))}
                        onClick={() => setDateList(current => {
                            return current.map(date => date.add(-1, "day"))
                        })}
                    >
                        <KeyboardArrowLeft fontSize="small" />
                    </IconButton>
                    <IconButton
                        sx={{ p: 0 }}
                        disabled={dateList.length === 0 || (dateList.length !== 0 && dateList[dateList.length - 1].add(1, "day").isAfter(maxDate, 'd'))}
                        onClick={() => setDateList(current => {
                            return current.map(date => date.add(1, "day"))
                        })}
                    >
                        <KeyboardArrowRight fontSize="small" />
                    </IconButton>
                    <IconButton
                        sx={{ p: 0 }}
                        disabled={dateList.length === 0 || (dateList.length !== 0 && dateList[dateList.length - 1].add(7, "day").isAfter(maxDate, 'd'))}
                        onClick={() => setDateList(current => {
                            return current.map(date => date.add(7, "day"))
                        })}
                    >
                        <KeyboardDoubleArrowRight fontSize="small" />
                    </IconButton>
                </Stack>
            </Stack>
        )
    }), [dateList, t, maxDate, minDate, setDateList]);

    const setFilter = useCallback((filterName: string, value: boolean) => {
        setTableActiveFilters((current) => ({
            ...current,
            [filterName]: value
        }));
    }, [setTableActiveFilters]);
    const doesExternalFilterPass = useCallback((node: RowNode) => {
        const { onlyAvailable } = tableActiveFilters;
        const pickupDate = formContext.getValues('basicDetails.pickupDate');
        if (!pickupDate)
            return true;
        const pickupDay = pickupDate.format(HEADER_FIELD_DATE_FORMAT);
        const availableCars = node.data?.[pickupDay]?.availableCars;
        if (onlyAvailable && availableCars <= 0)
            return false;
        return true;
    }, [tableActiveFilters]);
    const handleRowDataUpdated = useCallback((e: RowDataUpdatedEvent) => {
        const lockGroup = formContext.getValues('basicDetails.lockGroup');
        const pickupLocation = formContext.getValues('basicDetails.pickupLocation');
        const carGroup = formContext.getValues('basicDetails.carGroup');
        if (!pickupLocation || !carGroup) {
            return;
        }
        const index = e.api.getRowNode(lockGroup ? pickupLocation.id : carGroup.acrissCode)?.rowIndex;
        if (index === null || index === undefined) {
            return;
        }
        e.api.ensureIndexVisible(index, 'top');
    }, []);
    const getRowId = useCallback((params: GetRowIdParams) => {
        return params.data.code;
    }, []);

    useEffect(() => {
        const pickupDate = formContext.getValues('basicDetails.pickupDate');
        if (!pickupDate || !pickupDate.isValid() || totalRentalDays === 0)
            setDateList([]);
        else {
            let newDateList: dayjs.Dayjs[] = [];
            if (totalRentalDays < 10) {
                newDateList = _
                    .range(-3,  (totalRentalDays + 1) + 3)
                    .map(n => pickupDate.add(n, "day"));
            }
            else {
                newDateList = _.range(0, 10).map(n => pickupDate.add(n, "day").startOf('d'))
            }
            const daysDiff = dayjs()
                .startOf('d')
                .diff(newDateList[0].startOf('d'), 'd');
            setDateList(newDateList.map(d => {
                if (daysDiff > 0) {
                    return d.add(daysDiff, 'd')
                }
                return d;
            }));
        }
    }, [totalRentalDays]);
    useEffect(() => {
        const firstColumn: ColDef = {
            field: "name",
            headerName: lockGroup ? t('label.location') : t('label.carGroup'),
            valueFormatter: (params: ValueFormatterParams) => {
                return lockGroup ? params.data.label : `${params.data.code} - ${params.data.label}`
            },
            flex: 1,
            minWidth: 300,
        }
        const dateColumns: ColDef[] = dateList.map(date => ({
            field: date.format(HEADER_FIELD_DATE_FORMAT),
            headerName: date.format('DD/MM'),
            minWidth: 120,
            maxWidth: 120,
            headerClass: (params: any) => {
                if (!pickupDate || !dropoffDate) {
                    return;
                }
                const date = dayjs(params.colDef.field, HEADER_FIELD_DATE_FORMAT);
                const pickupDay = dayjs(pickupDate.format(HEADER_FIELD_DATE_FORMAT), HEADER_FIELD_DATE_FORMAT);
                const dropoffDay = dayjs(dropoffDate.format(HEADER_FIELD_DATE_FORMAT), HEADER_FIELD_DATE_FORMAT);
                return isBetweenDates(date, pickupDay, dropoffDay) ? 'ag-header-cell-highlighted' : undefined;
            },
            cellRenderer: (params: ICellRendererParams) => {
                const {
                    availableCars,
                    oneWayInS,
                    stopSells,
                } = params.value || {};

                if (!params.value)
                    return <></>;

                return (
                    <CellAvailabilities
                        availableCars={availableCars}
                        oneWayInS={oneWayInS}
                        stopSells={stopSells}
                    />
                )
            }
        }));
        setColumnDefs([
            firstColumn,
            ...dateColumns
        ]);
    }, [lockGroup, dateList, t]);
    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.onFilterChanged();

            const filteredRow =  [];
            gridRef.current.api.forEachNodeAfterFilter((node : any) => {filteredRow.push(node.data)})

            if(filteredRow.length < 1 ) gridRef?.current?.api.showNoRowsOverlay();
            else gridRef?.current?.api.hideOverlay();
        }
    }, [tableActiveFilters]);
    useEffect(() => {
        if (carsAvailabilitiesQuery.isFetching) {
            gridRef.current?.api?.showLoadingOverlay();
        }
    }, [carsAvailabilitiesQuery.isFetching]);
    useEffect(() => {
        if (!carsAvailabilitiesParams) {
            setRowData([]);
        }
    }, [carsAvailabilitiesParams])


    const isSelectedCarGroupUnavailable = useMemo(() => {
         // Controlli preliminari
         if(!tenant){
             return false;
         }
         if(tenant !== 'drivalia_es' ){
             return false;
         }
         if (!pickupDate || !carGroup || !rowData || rowData.length === 0) {
             return false;
         }

         // 1. La chiave nel JSON è formattata come DD/MM/YYYY (definito in HEADER_FIELD_DATE_FORMAT)
         const dateKey = pickupDate.format(HEADER_FIELD_DATE_FORMAT);

         // 2. Trova la riga corrispondente al carGroup selezionato (usando acrissCode come in rowClassRules)
         const selectedRow = rowData.find(row => row.code === carGroup.acrissCode);

         // 3. Estrai i dati per quella specifica data
         const dateData = selectedRow?.[dateKey];

         // 4. Restituisci true se availableCars esiste ed è esattamente 0
         //true disabilita il bottone
          return (dateData as any)?.availableCars <= 0;
    }, [pickupDate, carGroup, rowData, tenant]);

    // 2. Add this Effect to sync the local calculation with the global Recoil state
    useEffect(() => {
        setIsSelectedCarGroupUnavailableGlobal(isSelectedCarGroupUnavailable);
    }, [isSelectedCarGroupUnavailable, setIsSelectedCarGroupUnavailableGlobal]);

    return (
        <AgGridCustomTable
            gridRef={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            isExternalFilterPresent={() => true}
            doesExternalFilterPass={doesExternalFilterPass}
            animateRows
            localeText={agGridLocaleText}
            suppressContextMenu
            rowClassRules={rowClassRules}
            onRowDataUpdated={handleRowDataUpdated}
            getRowId={getRowId}
            pagination={false}
            toolbar={toolbar}
            height="700px"
        />
    );
}

export default TableAvailabilities;
